import cx from "classnames";
import Image from "next/image";
import { useRouter } from "next/router";
import { type FC, useMemo } from "react";

import {
	type DocumentMetadata,
	getDefaultUrlPrefix,
	makeUrlPrefix,
} from "@moment/api-collab/api-types";
import { LinkButton, isRunningInIframe, useTheme } from "@moment/ui";

import { MomentLogo } from "~/assets/MomentLogo";

import { PublishHeaderActions } from "../shared/PublishHeaderActions";

export const Header: FC<{
	workspaceName: string;
	documentMetadata: DocumentMetadata;
}> = ({ workspaceName, documentMetadata }) => {
	const { resolvedTheme } = useTheme();

	const urlPrefix = useMemo(() => {
		if (isRunningInIframe()) {
			return getDefaultUrlPrefix(workspaceName, documentMetadata.id);
		}
		return makeUrlPrefix(workspaceName, documentMetadata.id, documentMetadata.publishSettings);
	}, [workspaceName, documentMetadata.id, documentMetadata.publishSettings]);

	// Get the appropriate logo based on current theme
	const logoSrc = useMemo(() => {
		const logos = documentMetadata.publishSettings?.logo;
		if (!logos) return null;

		// Use dark logo for dark theme, light logo for light theme
		if ((resolvedTheme === "dark" || resolvedTheme === "oscar-mayer") && logos.dark)
			return logos.dark;
		if (resolvedTheme === "light" && logos.light) return logos.light;

		// Fallback: use any available logo if theme-specific one isn't available
		return logos.light || logos.dark || null;
	}, [resolvedTheme, documentMetadata.publishSettings?.logo]);

	return (
		<div className={cx("flex items-center gap-2 -ml-3 bg-primary flex-shrink-0 h-16")}>
			<LinkButton
				preset="quaternary"
				href={urlPrefix}
				size="md"
				className="[&_svg]:!w-24 [&_svg]:!h-auto [&_svg]:text-primary [&_img]:!w-24 [&_img]:!h-auto"
			>
				{logoSrc ? (
					<Image src={logoSrc} alt="Logo" className="object-contain" />
				) : (
					<MomentLogo />
				)}
			</LinkButton>
			<PublishHeaderActions
				documentMetadata={documentMetadata}
				className="ml-auto flex items-center gap-2"
			/>
		</div>
	);
};
